import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { AckResponse, RoomError, RoomJoinResult, RoomSnapshot } from '@deck-after-dark/shared';
import { GameContext, type ConnectionStatus, type GameContextValue } from './gameContext';
import {
  clearSession,
  loadStoredSession,
  saveSession,
} from './gameStorage';
import {
  connectGameSocket,
  disconnectGameSocket,
  getGameSocket,
  unwrapAck,
} from './gameSocket';
import { getCurrentPlayer } from './mockGameReducer';
import type { MockGameState } from './mockGameTypes';
import {
  EMPTY_SERVER_UI_STATE,
  mapClientSettingsToServer,
  snapshotToGameState,
} from './snapshotAdapter';
import {
  clearCardSelection,
  formatServerError,
  isSelectionComplete,
  toggleCardSelection,
  wildCardsInSelection,
  writeInsForSelection,
} from './cardSelection';

function routeForPhase(phase: MockGameState['phase']): string | null {
  switch (phase) {
    case 'lobby':
    case 'none':
      return '/lobby';
    case 'playing':
      return '/game';
    case 'judging':
      return '/judge';
    case 'roundResult':
      return '/round-winner';
    case 'gameOver':
      return '/game-over';
    default:
      return null;
  }
}

export function ServerGameProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [exitDestination, setExitDestination] = useState<string | null>(null);
  useEffect(() => {
    if (exitDestination === location.pathname) setExitDestination(null);
  }, [exitDestination, location.pathname]);

  const [restoring, setRestoring] = useState(() => Boolean(loadStoredSession()));
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [writeInText, setWriteInText] = useState('');
  const [actionPending, setActionPending] = useState(false);


  const state: MockGameState = useMemo(() => {
    if (!snapshot) {
      return { ...EMPTY_SERVER_UI_STATE, joinError };
    }
    return snapshotToGameState({
      snapshot,
      selectedCardIds,
      writeInText,
      joinError,
    });
  }, [snapshot, selectedCardIds, writeInText, joinError]);

  const currentPlayer = getCurrentPlayer(state);

  const showError = useCallback((message: string) => {
    const formatted = formatServerError(message);
    setLastError(`${formatted.title} — ${formatted.detail}`);
  }, []);

  const handleRoomError = useCallback((error: RoomError) => {
    const formatted = formatServerError(`${error.code}: ${error.message}`);
    setLastError(`${formatted.title} — ${formatted.detail}`);
  }, []);

  const applySnapshot = useCallback((next: RoomSnapshot) => {
    setSnapshot(next);
    setJoinError(null);
  }, []);

  const ensureConnected = useCallback(async () => {
    setConnectionStatus((prev: ConnectionStatus) => (prev === 'connected' ? prev : 'connecting'));
    await connectGameSocket();
    setConnectionStatus('connected');
  }, []);

  const emitAction = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      setActionPending(true);
      try {
        await ensureConnected();
        return await fn();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Request failed.';
        showError(message);
        return null;
      } finally {
        setActionPending(false);
      }
    },
    [ensureConnected, showError],
  );

  // Rebind the saved seat on EVERY transport connection, including a refresh.
  useEffect(() => {
    let cancelled = false;
    const socket = getGameSocket();
    const resume = async () => {
      const stored = loadStoredSession();
      if (!stored) { setConnectionStatus('connected'); setRestoring(false); return; }
      setConnectionStatus('reconnecting');
      try {
        const res = (await socket.emitWithAck('room:join', {
          roomCode: stored.roomCode, name: stored.playerName, playerId: stored.playerId,
        })) as AckResponse<RoomJoinResult>;
        if (cancelled) return;
        if (res.ok) {
          applySnapshot(res.data.snapshot);
          saveSession(res.data.roomCode, res.data.playerId, stored.playerName);
          setConnectionStatus('connected');
          const next = snapshotToGameState({ snapshot: res.data.snapshot });
          navigate(routeForPhase(next.phase) ?? '/lobby', { replace: true });
        } else {
          if (['ROOM_NOT_FOUND', 'PLAYER_KICKED', 'ROOM_NOT_JOINABLE'].includes(res.error.code)) clearSession();
          setSnapshot(null);
          showError(res.error.message);
          setJoinError(res.error.message);
          setExitDestination('/join');
        }
      } catch {
        if (!cancelled) showError('Reconnection failed. Your seat is saved; refresh to try again.');
      } finally {
        if (!cancelled) setRestoring(false);
      }
    };
    const disconnected = () => setConnectionStatus('disconnected');
    const reconnecting = () => setConnectionStatus('reconnecting');
    const failed = () => {
      setConnectionStatus('disconnected');
      setRestoring(false);
      showError('Could not connect. Your saved session will resume when the server is available.');
    };
    const kicked = ({ message }: { message: string }) => {
      clearSession();
      setSnapshot(null);
      setSelectedCardIds([]);
      setWriteInText('');
      setRestoring(false);
      showError(`PLAYER_KICKED: ${message}`);
      setExitDestination('/');
    };
    const offline = () => { socket.disconnect(); setConnectionStatus('disconnected'); };
    const online = () => { setConnectionStatus('reconnecting'); socket.connect(); };
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    socket.on('room:snapshot', applySnapshot);
    socket.on('room:error', handleRoomError);
    socket.on('room:kicked', kicked);
    socket.on('connect', resume);
    socket.on('disconnect', disconnected);
    socket.on('connect_error', failed);
    socket.io.on('reconnect_attempt', reconnecting);
    setConnectionStatus('connecting');
    if (socket.connected) void resume();
    else socket.connect();
    return () => {
      cancelled = true;
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
      socket.off('room:snapshot', applySnapshot);
      socket.off('room:error', handleRoomError);
      socket.off('room:kicked', kicked);
      socket.off('connect', resume);
      socket.off('disconnect', disconnected);
      socket.off('connect_error', failed);
      socket.io.off('reconnect_attempt', reconnecting);
      disconnectGameSocket();
    };
  // Socket lifetime belongs to the provider, not the current route.
  }, []);

  // Clear local selection when the authoritative round changes.
  const prevRoundRef = useRef<number | null>(null);
  useEffect(() => {
    const roundNumber = snapshot?.game?.round?.roundNumber;
    if (roundNumber === undefined) return;
    if (prevRoundRef.current !== null && prevRoundRef.current !== roundNumber) {
      setSelectedCardIds([]);
      setWriteInText('');
    }
    prevRoundRef.current = roundNumber;
  }, [snapshot?.game?.round?.roundNumber]);
  useEffect(() => {
    if (!snapshot?.started) return;
    const target = routeForPhase(state.phase);
    if (!target || location.pathname === target) return;
    if (target === '/game' && location.pathname === '/write-in') return;
    // Don't yank users off create/join/home unless they have an active room.
    const gameplayRoutes = ['/game', '/write-in', '/judge', '/round-winner', '/game-over', '/lobby'];
    if (!gameplayRoutes.includes(location.pathname) && target === '/lobby') return;
    navigate(target);
  }, [state.phase, snapshot?.started, location.pathname, navigate]);

  const doSubmitCards = useCallback(
    (cardIds: string[], writeIns?: string[]) => {
      void emitAction(async () => {
        const socket = getGameSocket();
        unwrapAck(
          await socket.emitWithAck('game:submitCards', {
            cardIds,
            ...(writeIns?.length ? { writeIns } : {}),
          }),
        );
        setSelectedCardIds([]);
        setWriteInText('');
      });
    },
    [emitAction],
  );

  const buildSubmitPayload = useCallback(() => {
    const pick = snapshot?.game?.round?.pick ?? 1;
    if (!isSelectionComplete(selectedCardIds, pick)) return null;
    const wilds = wildCardsInSelection(selectedCardIds, state.hand);
    const writeIns = writeInsForSelection(selectedCardIds, state.hand, writeInText);
    if (wilds.length > 0 && writeIns.length !== wilds.length) return null;
    return {
      cardIds: [...selectedCardIds],
      ...(writeIns.length ? { writeIns } : {}),
    };
  }, [snapshot, selectedCardIds, state.hand, writeInText]);

  const value = useMemo<GameContextValue>(
    () => ({
      runtimeMode: 'server',
      connectionStatus,
      state,
      currentPlayer,
      lastError,
      clearJoinError: () => setJoinError(null),
      clearError: () => setLastError(null),

      createRoom(_roomName, nickname, settings) {
        void emitAction(async () => {
          const socket = getGameSocket();
          const res = unwrapAck(
            await socket.emitWithAck('room:create', {
              name: nickname || 'Host',
              settings: mapClientSettingsToServer(settings),
            }),
          );
          saveSession(res.roomCode, res.playerId, nickname || 'Host');
          applySnapshot(res.snapshot);
          navigate('/lobby');
        });
      },

      joinRoom(roomCode, nickname) {
        void emitAction(async () => {
          const socket = getGameSocket();
          const stored = loadStoredSession();
          const res = (await socket.emitWithAck('room:join', {
            roomCode: roomCode.toUpperCase(),
            name: nickname,
            ...(stored?.roomCode === roomCode.toUpperCase() ? { playerId: stored.playerId } : {}),
          })) as AckResponse<RoomJoinResult>;
          if (!res.ok) {
            setJoinError(res.error.message);
            return false;
          }
          const data = res.data;
          saveSession(data.roomCode, data.playerId, nickname);
          applySnapshot(data.snapshot);
          navigate('/lobby');
          return true;
        });
        return true;
      },

      kickPlayer(playerId) {
        void emitAction(async () => {
          unwrapAck(await getGameSocket().emitWithAck('room:kick', { playerId }));
        });
      },

      toggleReady() {
        if (!snapshot) return;
        const me = snapshot.lobby.players.find((p) => p.id === snapshot.you);
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(await socket.emitWithAck('player:ready', { ready: !(me?.isReady ?? false) }));
        });
      },

      startGame() {
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(await socket.emitWithAck('game:start', {}));
        });
      },

      toggleCard(id) {
        const pick = snapshot?.game?.round?.pick ?? 1;
        setSelectedCardIds((prev) => toggleCardSelection(prev, id, pick));
      },

      clearSelection() {
        setSelectedCardIds(clearCardSelection());
        setWriteInText('');
      },

      setWriteIn(text) {
        setWriteInText(text);
      },

      submitAnswer() {
        if (!snapshot?.game || actionPending) return;
        const pick = snapshot.game.round?.pick ?? 1;
        if (!isSelectionComplete(selectedCardIds, pick)) return;

        const wilds = wildCardsInSelection(selectedCardIds, state.hand);
        if (wilds.length > 0 && !writeInText.trim()) {
          navigate('/write-in');
          return;
        }

        const payload = buildSubmitPayload();
        if (!payload) return;
        doSubmitCards(payload.cardIds, payload.writeIns);
      },

      confirmWriteIn() {
        const text = writeInText.trim();
        if (!text) return false;
        const payload = buildSubmitPayload();
        if (!payload) return false;
        doSubmitCards(payload.cardIds, payload.writeIns);
        return true;
      },

      pickWinner(playerId) {
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(await socket.emitWithAck('game:pickWinner', { winnerPlayerId: playerId }));
        });
      },

      pickWinnerByIndex(revealIndex) {
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(
            await socket.emitWithAck('game:pickWinnerByIndex', { revealIndex }),
          );
        });
      },

      nextRound() {
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(await socket.emitWithAck('game:advanceRound'));
        });
      },

      playAgain() {
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(await socket.emitWithAck('room:leave'));
          // Clear the snapshot so the phase-routing effect goes inert (no bounce
          // back into the finished game). GameOverPage tolerates this via its
          // mount-time validity guard, so it won't redirect to "/" mid-exit.
          setExitDestination('/create');
          clearSession();
          setSnapshot(null);
          showError('Game over — create a new room to play again.');
        });
      },

      newRoom() {
        void emitAction(async () => {
          const socket = getGameSocket();
          unwrapAck(await socket.emitWithAck('room:leave'));
          setExitDestination('/create');
          clearSession();
          setSnapshot(null);
        });
      },

      reset() {
        void emitAction(async () => {
          const socket = getGameSocket();
          if (snapshot) unwrapAck(await socket.emitWithAck('room:leave'));
          setExitDestination('/');
          clearSession();
          setSnapshot(null);
        });
      },
    }),
    [
      connectionStatus,
      state,
      currentPlayer,
      lastError,
      snapshot,
      writeInText,
      selectedCardIds,
      actionPending,
      emitAction,
      applySnapshot,
      doSubmitCards,
      buildSubmitPayload,
      navigate,
      showError,
    ],
  );

  return <GameContext.Provider value={value}>{exitDestination && location.pathname !== exitDestination ? <Navigate to={exitDestination} replace /> : restoring && !snapshot ? (
    <main className="min-h-[100dvh] grid place-items-center p-6">
      <p role="status" className="text-primary">Rejoining your table?</p>
    </main>
  ) : children}</GameContext.Provider>;
}
